import React, { useState } from 'react';
import styled from 'styled-components';
import Col from 'react-bootstrap/Col';
import Row from 'react-bootstrap/Row';

import MergeItem from './MergeItem';
import { ContactsSyncApi } from '../api/ContactsSyncApi';
import ContactsSpinner from './ContactsSpinner';
import { useErrorContext } from '../contexts/ErrorContext';
import { useUserContext } from '../contexts/UserContext';

import { TAppContact } from 'TProtocol/common/models';
import { TMergeAcceptance } from 'TProtocol/contactssync/messages';

interface Props {
  contacts: TAppContact[] | undefined;
  mergeSuggestionId: string;
  onClickMergeSuggestion: (mergeSuggestionId: string) => void;
}

function MergePair(props: Props) {
  const errorContext = useErrorContext();
  const userContext = useUserContext();
  const [isProcessing, setIsProcessing] = useState<boolean>(false);

  const onMerge = async () => {
    setIsProcessing(true);

    if (props.contacts?.[0]?.contactId && props.contacts?.[1]?.contactId) {
      const contactIds = [props.contacts[0].contactId, props.contacts[1].contactId];

      try {
        const response = await ContactsSyncApi.updateSuggestedMerges(
          userContext.getAuthHeader(), contactIds, TMergeAcceptance.YES
        );
        if (response.success) {
          setIsProcessing(false);
          props.onClickMergeSuggestion(props.mergeSuggestionId);
        } else {
          errorContext.error('Merge failed.');
        }
      } catch (e) {
        setIsProcessing(false);
      }
    }
  };

  const onDismiss = async () => {
    setIsProcessing(true);
    if (props.contacts && props.contacts.length > 0 && props.contacts[0].contactId && props.contacts[1].contactId) {
      const contactIds = [props.contacts[0].contactId, props.contacts[1].contactId];

      try {
        const response = await ContactsSyncApi.updateSuggestedMerges(
          userContext.getAuthHeader(), contactIds, TMergeAcceptance.NO
        );
        if (response.success) {
          setIsProcessing(false);
          props.onClickMergeSuggestion(props.mergeSuggestionId);
        } else {
          errorContext.error('Merge failed.');
        }
      } catch (e) {
        setIsProcessing(false);
      }
    }
  };

  const onUnsure = async () => {
    setIsProcessing(true);
    if (props.contacts && props.contacts.length > 0 && props.contacts[0].contactId && props.contacts[1].contactId) {
      const contactIds = [props.contacts[0].contactId, props.contacts[1].contactId];

      try {
        const response = await ContactsSyncApi.updateSuggestedMerges(
          userContext.getAuthHeader(), contactIds, TMergeAcceptance.UNSURE
        );
        if (response.success) {
          setIsProcessing(false);
          props.onClickMergeSuggestion(props.mergeSuggestionId);
        } else {
          errorContext.error('Merge failed.');
        }
      } catch (e) {
        setIsProcessing(false);
      }
    }
  };

  if (!props.contacts || props.contacts.length != 2) {
    return <></>;
  }

  return (
    <MergePairContainer>
      <Row>
        <Col>
          <MergeItem contact={props.contacts[0]}/>
        </Col>
        <Col>
          <MergeItem contact={props.contacts[1]}/>
        </Col>
      </Row>

      <Row>
        <Col>
          {isProcessing ?
            <ContactsSpinner/> :
            <LCMButtons>
              <LCMButtonsInner>
                <MergeButton onClick={onMerge}>Same</MergeButton>
                <MergeButtonSecondary onClick={onDismiss}>Different</MergeButtonSecondary>
                <MergeButtonSecondary onClick={onUnsure}>Not sure</MergeButtonSecondary>
              </LCMButtonsInner>
            </LCMButtons>
          }
        </Col>
      </Row>
    </MergePairContainer>
  );
}

const MergePairContainer = styled.div`
  max-width:900px;
  margin-bottom:12px;
  border-radius: 5px;
`;

const LCMButtons = styled.div`
  margin-top:16px;
  width:100%;
  display:flex;
  align-content: flex-end;
`;

const LCMButtonsInner = styled.div`
  margin-left: auto;
  margin-right: 4px;
`

const MergeButton = styled.button`
  &, &:active, &:focus {
    width: 80px;
    padding: 0 12px;
    height: 40px;
    border-radius: 4px;
    box-shadow: 0 2px 4px 0 rgba(54, 123, 245, 0.24), 0 4px 8px 0 rgba(54, 123, 245, 0.16);
    border: solid 0.5px #0183c4;
    background-color: #009bdf;
    font-size: 12px;
    font-weight: 500;
    color: #ffffff;
    margin-bottom: 25px;
  }
`;

const MergeButtonSecondary = styled(MergeButton)`
  &, &:active, &:focus {
    color: #009bdf;
    border-radius: 4px;
    box-shadow: none;
    border: solid 1px #009bdf;
    background-color: #fff;
    margin-left: 5px;
  }
  
  &:hover {
    box-shadow: 1px 1px 4px 0 rgba(54, 123, 245, 0.14), 2px 2px 8px 0 rgba(54, 123, 245, 0.06);
  }
`;

export default MergePair;
